import React from "react";
import { Radio as MuiRadio, RadioProps as MuiRadioProps } from "@mui/material";

export type RadioSize = "sm" | "md";

export interface RadioProps extends Omit<
  MuiRadioProps,
  // Locked: the glyphs come from the design system's icon set, and styling
  // is owned by tokens and the props below rather than per-instance `sx`.
  "sx" | "icon" | "checkedIcon" | "size"
> {
  /** Radio size. Default: 'md' */
  size?: RadioSize;
  /** Disables the touch ripple effect. Passed through to MUI. */
  disableRipple?: boolean;
}

const sizeMap: Record<RadioSize, MuiRadioProps["size"]> = {
  sm: "small",
  md: "medium",
};

/** Wraps MUI Radio; `color` and `size` are constrained to the Centro palette roles and sm/md scale. */
// The ref type is taken from MUI rather than named outright: the root renders
// as a `<span>`, but MUI inherits ButtonBase's typing and declares it an
// `HTMLButtonElement`, so naming either one would be wrong somewhere.
export const Radio = React.forwardRef<
  React.ComponentRef<typeof MuiRadio>,
  RadioProps
>(({ size = "md", color = "primary", ...rest }, ref) => (
  <MuiRadio
    data-testid="ds-radio"
    ref={ref}
    size={sizeMap[size]}
    color={color}
    {...rest}
  />
));

Radio.displayName = "Radio";
